import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Fab,
  Container,
  Snackbar,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  Add,
  CloudUpload,
  Image as ImageIcon,
  Visibility,
  Edit,
  ViewModule,
  ViewList,
  Error as ErrorIcon,
  ExpandMore,
  ExpandLess,
  CheckCircle,
} from '@mui/icons-material';
import { AddProductDialog, ProductCard, ProductsStatsCards, EmptyProductsState } from '../components/products';
import { LoadingSpinner, SearchBar, StatusChip } from '../components/common';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchProducts,
  fetchBrands,
  fetchCategories,
  createProduct,
  bulkUploadProducts,
  createBrand,
  createCategory,
  setSelectedProduct,
  clearError,
} from '../store/slices/productsSlice';
import { Product, Brand, Category, BulkUploadRow, BulkUploadResult } from '../types/api/products';


const ProductsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  
  const {
    products,
    brands,
    categories,
    selectedProduct,
    bulkUploadResult,
    bulkUploading,
    loading,
    error
  } = useAppSelector((state) => state.products);

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkResultDialogOpen, setBulkResultDialogOpen] = useState(false);
  const [showBulkErrors, setShowBulkErrors] = useState(false);
  const [showBulkSuccess, setShowBulkSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');

  useEffect(() => {
    dispatch(fetchProducts({}));
    dispatch(fetchBrands());
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    if (bulkUploadResult && !bulkResultDialogOpen) {
      setBulkResultDialogOpen(true);
      
      if (bulkUploadResult.successful > 0) {
        showSnackbar(
          `Bulk upload completed! ${bulkUploadResult.successful} products created, ${bulkUploadResult.failed} failed.`,
          bulkUploadResult.failed > 0 ? 'warning' : 'success'
        );
      } else {
        showSnackbar('Bulk upload failed. Please check the errors.', 'error');
      }
    }
  }, [bulkUploadResult]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleAddProduct = async (productData: any, images: File[]) => {
    try {
      const createProductData = {
        ...productData,
        images
      };
      
      await dispatch(createProduct(createProductData)).unwrap();
      await dispatch(fetchProducts({}));
      showSnackbar('Product added successfully!', 'success');
      setAddDialogOpen(false);
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to add product', 'error');
      throw err;
    }
  };

  const handleBulkUpload = async (csvData: BulkUploadRow[]) => {
    try {
      const csvHeaders = ['name', 'description', 'brand_name', 'category_name', 'sku', 'manufacturer', 'country_of_origin', 'price_range_min', 'price_range_max', 'product_url'];
      const csvContent = [
        csvHeaders.join(','),
        ...csvData.map(row => [
          row.name,
          row.description,
          row.brand_name,
          row.category_name,
          row.sku,
          row.manufacturer,
          row.country_of_origin,
          row.price_range_min,
          row.price_range_max,
          row.product_url
        ].map(field => `"${field}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const csvFile = new File([blob], 'bulk_upload.csv', { type: 'text/csv' });

      await dispatch(bulkUploadProducts(csvFile)).unwrap();
      setAddDialogOpen(false);
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to process bulk upload', 'error');
      throw err;
    }
  };

  const loadBrands = async () => {
    dispatch(fetchBrands());
  };

  const loadCategories = async () => {
    dispatch(fetchCategories());
  };

  const handleCreateBrand = async (brandData: { name: string; description: string }) => {
    try {
      await dispatch(createBrand(brandData)).unwrap();
      showSnackbar('Brand created successfully!', 'success');
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to create brand', 'error');
      throw err;
    }
  };

  const handleCreateCategory = async (categoryData: { name: string; description: string }) => {
    try {
      await dispatch(createCategory(categoryData)).unwrap();
      showSnackbar('Category created successfully!', 'success');
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to create category', 'error');
      throw err;
    }
  };


  const handleViewProduct = (product: Product) => {
    dispatch(setSelectedProduct(product));
    setViewDialogOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    dispatch(setSelectedProduct(product));
    setEditDialogOpen(true);
  };

  const filteredProducts = Array.isArray(products) ? products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.brand_name.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  if (loading) {
    return <LoadingSpinner message="Loading products..." />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {bulkUploading && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          zIndex: 1300,
          bgcolor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh'
        }}>
          <Paper sx={{ p: 4, textAlign: 'center', minWidth: 300 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Processing Bulk Upload
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Scraping product data and images...
            </Typography>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This may take a few minutes depending on the number of products
            </Typography>
          </Paper>
        </Box>
      )}

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" fontWeight="bold" color="primary">
              Product Catalog
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage your authentic product database with URL scraping
            </Typography>
          </Box>
          <Fab
            color="primary"
            onClick={() => setAddDialogOpen(true)}
            sx={{ boxShadow: 4 }}
          >
            <Add />
          </Fab>
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search products by name or brand..."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('grid')}
                  startIcon={<ViewModule />}
                >
                  Grid
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('list')}
                  startIcon={<ViewList />}
                >
                  List
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert 
          severity={error.includes('successfully') ? 'success' : 'error'} 
          sx={{ mb: 3 }}
          onClose={() => dispatch(clearError())}
        >
          {error}
        </Alert>
      )}

      <ProductsStatsCards products={products} brands={brands} />

      {filteredProducts.length === 0 ? (
        <EmptyProductsState
          searchTerm={searchTerm}
          onAddProduct={() => setAddDialogOpen(true)}
        />
      ) : (
        <>
          {viewMode === 'grid' ? (
            <Grid container spacing={3}>
              {filteredProducts.map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <ProductCard
                    product={product}
                    onView={handleViewProduct}
                    onEdit={handleEditProduct}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={product.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={product.primary_image_url ? `https://server.bitsindia.in${product.primary_image_url}` : undefined}
                            sx={{ width: 40, height: 40 }}
                          >
                            <ImageIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {product.product_id.slice(0, 8)}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{product.brand_name}</TableCell>
                      <TableCell>{product.category_name}</TableCell>
                      <TableCell>
                        <StatusChip status={product.verification_status} variant="verification" />
                      </TableCell>
                      <TableCell>
                        {new Date(product.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewProduct(product)}
                          title="View Product"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleEditProduct(product)}
                          title="Edit Product"
                        >
                          <Edit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <AddProductDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        brands={brands}
        categories={categories}
        onAddProduct={handleAddProduct}
        onBulkUpload={handleBulkUpload}
        onCreateBrand={handleCreateBrand}
        onCreateCategory={handleCreateCategory}
        onLoadBrands={loadBrands}
        onLoadCategories={loadCategories}
      />

      <Dialog 
        open={bulkResultDialogOpen} 
        onClose={() => setBulkResultDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUpload />
            Bulk Upload Results
          </Box>
        </DialogTitle>
        <DialogContent>
          {bulkUploadResult && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {bulkUploadResult.total_rows}
                    </Typography>
                    <Typography variant="body2">Total Rows</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {bulkUploadResult.successful}
                    </Typography>
                    <Typography variant="body2">Successful</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">
                      {bulkUploadResult.failed}
                    </Typography>
                    <Typography variant="body2">Failed</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="text.secondary">
                      {Math.round((bulkUploadResult.successful / bulkUploadResult.total_rows) * 100)}%
                    </Typography>
                    <Typography variant="body2">Success Rate</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {bulkUploadResult.created_products.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    onClick={() => setShowBulkSuccess(!showBulkSuccess)}
                    startIcon={showBulkSuccess ? <ExpandLess /> : <ExpandMore />}
                    color="success"
                    sx={{ mb: 1 }}
                  >
                    Successfully Created Products ({bulkUploadResult.created_products.length})
                  </Button>
                  <Collapse in={showBulkSuccess}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <List dense>
                        {bulkUploadResult.created_products.map((product, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <CheckCircle color="success" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Row ${product.row}: ${product.name}`}
                              secondary={`${product.brand} • ${product.category} • ${product.images_downloaded} images downloaded`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Collapse>
                </Box>
              )}

              {bulkUploadResult.errors.length > 0 && (
                <Box>
                  <Button
                    onClick={() => setShowBulkErrors(!showBulkErrors)}
                    startIcon={showBulkErrors ? <ExpandLess /> : <ExpandMore />}
                    color="error"
                    sx={{ mb: 1 }}
                  >
                    Errors ({bulkUploadResult.errors.length})
                  </Button>
                  <Collapse in={showBulkErrors}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <List dense>
                        {bulkUploadResult.errors.map((error, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <ErrorIcon color="error" />
                            </ListItemIcon>
                            <ListItemText
                              primary={error}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Collapse>
                </Box>
              )}

              {bulkUploadResult.successful > 0 && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Upload completed successfully!
                  </Typography>
                  <Typography variant="body2">
                    {bulkUploadResult.successful} products have been created and added to your catalog. 
                    Images have been automatically scraped and saved from the provided URLs.
                  </Typography>
                </Alert>
              )}

              {bulkUploadResult.failed > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Some products failed to upload
                  </Typography>
                  <Typography variant="body2">
                    {bulkUploadResult.failed} products failed to upload. Please check the error details above 
                    and fix the issues in your CSV file before trying again.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkResultDialogOpen(false)}>Close</Button>
          {bulkUploadResult && bulkUploadResult.successful > 0 && (
            <Button 
              variant="contained" 
              onClick={() => {
                setBulkResultDialogOpen(false);
              }}
            >
              View Updated Catalog
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Product Details</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    height: 300, 
                    bgcolor: 'grey.100', 
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundImage: selectedProduct.primary_image_url 
                      ? `url(https://server.bitsindia.in${selectedProduct.primary_image_url})`
                      : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}>
                    {!selectedProduct.primary_image_url && (
                      <ImageIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    {selectedProduct.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    ID: {selectedProduct.product_id}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Brand:</strong> {selectedProduct.brand_name}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Category:</strong> {selectedProduct.category_name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2">
                      <strong>Status:</strong>
                    </Typography>
                    <StatusChip status={selectedProduct.verification_status} variant="verification" />
                  </Box>
                  {selectedProduct.description && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Description:</strong> {selectedProduct.description}
                    </Typography>
                  )}
                  {selectedProduct.price_range_min && selectedProduct.price_range_max && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Price Range:</strong> ${selectedProduct.price_range_min} - ${selectedProduct.price_range_max}
                    </Typography>
                  )}
                  <Typography variant="body2" gutterBottom>
                    <strong>Created:</strong> {new Date(selectedProduct.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<Edit />}
            onClick={() => {
              setViewDialogOpen(false);
              if (selectedProduct) {
                handleEditProduct(selectedProduct);
              }
            }}
          >
            Edit Product
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2 }}>
            Edit functionality will be implemented in the next iteration.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductsPage;